import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import ClearIcon from '@mui/icons-material/Clear';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SegmentOutlinedIcon from '@mui/icons-material/SegmentOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { IconButton, Paper, Stack, styled, TextField, useTheme } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { SelectConfig } from '../../../../../components/Form/Configuration/SelectConfig';
import { RendererType } from '../../../../../data-models/field-definition';
import { IField, KpiTemplateSectionType } from '../../../../../data-models/field2.data-model';
import { IKPITemplateSectionDataModel } from '../../../../../data-models/kpi-template.data-model';
import { KPITableConfig } from './KPITableConfig';
import { ParagraphConfig } from './ParagraphConfig';
import { TextualKpiSelector } from './TextualKpiSelector';

const BorderlessTextField = styled(TextField)(({ theme }) => ({
  ...theme.typography.body2,
  background: 'transparent',
  width: '100%',
  padding: 0,
  '& .MuiOutlinedInput-input': {
    background: 'transparent',
    padding: 0,
    borderColor: theme.colors.secondary[60],
    color: theme.colors.neutral[70],
    '&::placeholder': {
      color: theme.colors.primary[80],
    },
    fontSize: '.875rem',
    '& fieldset': { border: 'none' },
    '& .MuiInputBase-root': {
      minHeight: '0px',
    },
  },
}));

const SectionTextWrapper = styled(Paper)(({ theme }) => ({
  height: 'fit-content',
  ...theme.typography.body2,
  color: 'white',
  padding: '1rem',
  width: '100%',
}));

function IconBySectionType(type: string) {
  switch (type) {
    case RendererType.text:
      return <SegmentOutlinedIcon color='primary' />;
    case RendererType.fileUpload:
      return <InsertDriveFileOutlinedIcon color='primary' />;
    default:
      return <TimelineOutlinedIcon color='primary' />;
  }
}

interface IKPIBuildItemFactoryProps {
  onRemoveSection: () => void;
  sectionRef: string;
  attributes: DraggableAttributes;
  listeners?: SyntheticListenerMap;
  textualKpiOptions: IField<unknown>[];
}

export function KPIBuildItemFactory(props: IKPIBuildItemFactoryProps) {
  const { onRemoveSection, sectionRef, attributes, listeners, textualKpiOptions } = props;
  const { colors } = useTheme();
  const { register, watch } = useFormContext();
  const { type } = watch(sectionRef) as IKPITemplateSectionDataModel;
  const sectionMetaRef = `${sectionRef}.meta`;
  const titlePath = getPathFromType(sectionMetaRef, type);
  const descriptionPath = `${sectionMetaRef}.description`;

  const field = useWatch({ name: sectionMetaRef });
  const rendererType =
    type === KpiTemplateSectionType.textualKpi
      ? RendererType.text
      : ((field as IField<unknown>).formMeta?.renderer?.type ?? '');

  return (
    <SectionTextWrapper
      elevation={0}
      style={type === KpiTemplateSectionType.textualKpi ? { minHeight: '6rem' } : undefined}
    >
      <Stack gap='1rem'>
        <Stack
          direction='row'
          gap='.5rem'
          sx={{
            '&:hover': {
              '& div#section-icon': {
                display: 'none',
              },
              '& div#section-drag-action-btn': {
                display: 'inline-flex !important',
              },
            },
          }}
        >
          <Stack
            alignItems='center'
            justifyContent='center'
            sx={{
              height: '44px',
              width: '44px',
              borderRadius: '4px',
              background: colors.secondary[10],
            }}
          >
            <Stack id='section-icon' justifyContent={'center'} alignItems={'center'}>
              {IconBySectionType(rendererType)}
            </Stack>
            <div
              {...attributes}
              {...listeners}
              id='section-drag-action-btn'
              style={{ cursor: 'move', display: 'none' }}
            >
              <DragIndicatorOutlinedIcon color='primary' />
            </div>
          </Stack>
          <Stack
            sx={{
              width: '100%',
              padding: 0,
            }}
          >
            {type === KpiTemplateSectionType.textualKpi ? (
              <TextualKpiSelector fieldPath={titlePath} options={textualKpiOptions} />
            ) : (
              <BorderlessTextField
                placeholder='Section title'
                {...register(titlePath)}
                sx={{
                  margin: 0,
                  padding: 0,
                  '& fieldset': { border: 'none' },
                  '& .MuiInputBase-root': {
                    minHeight: '0px',
                  },
                }}
              />
            )}
            {type !== KpiTemplateSectionType.textualKpi && (
              <BorderlessTextField
                placeholder='Description'
                {...register(descriptionPath)}
                sx={{
                  margin: 0,
                  marginTop: '.25rem',
                  '& .MuiInputBase-root': {
                    minHeight: '0px',
                  },
                  '& fieldset': { border: 'none' },
                }}
              />
            )}
          </Stack>
          <Stack>
            <IconButton onClick={onRemoveSection}>
              <ClearIcon />
            </IconButton>
          </Stack>
        </Stack>
        <KPIBuildItem sectionMetaRef={sectionMetaRef} />
      </Stack>
    </SectionTextWrapper>
  );
}

interface KPIPreviewItemProps {
  sectionMetaRef: string;
}

export function KPIBuildItem(props: KPIPreviewItemProps) {
  const { sectionMetaRef } = props;
  const field = useWatch({ name: sectionMetaRef });

  if (field.metrics) {
    return <KPITableConfig metricsRef={`${sectionMetaRef}.metrics`} />;
  } else {
    const type = (field as IField<unknown>).formMeta?.renderer?.type ?? '';

    return <FieldPreviewItem type={type} sectionRef={sectionMetaRef} />;
  }
}

interface IFieldPreviewItemProps {
  type: RendererType | string;
  sectionRef: string;
}

function FieldPreviewItem(props: IFieldPreviewItemProps) {
  const { type, sectionRef } = props;

  switch (type) {
    case RendererType.multiSelect:
      return <SelectConfig valuesPath={`${sectionRef}.formMeta.renderer.config.values`} />;

    case RendererType.text:
      return <ParagraphConfig sectionRef={props.sectionRef} />;

    default:
      return null;
  }
}

function getPathFromType(basePath: string, type?: string) {
  let titlePath = `${basePath}.entityField`;
  if (type === KpiTemplateSectionType.grid) {
    titlePath = `${basePath}.displayName`;
  }
  if (type === KpiTemplateSectionType.textualKpi) {
    titlePath = `${basePath}.kpiConfig`;
  }
  return titlePath;
}
